import type { Context, Plugin } from '@segment/analytics-next';

// The collector serves Segment's one-letter paths at its root (no `/v1`); its WAF blocks any other route
const PATHS = {
  track: 't',
  page: 'p',
  identify: 'i',
  group: 'g',
  alias: 'a',
} as const;

type CollectorEventType = keyof typeof PATHS;

// Browsers reject keepalive requests once all in-flight keepalive bodies exceed 64 KiB.
// The budget is shared with other SDKs (e.g. Sentry), so leave headroom.
const KEEPALIVE_BUDGET = 60_000;

/**
 * Destination plugin that copies every event to our in-house collector
 * alongside Segment. It reads the enriched `ctx.event`, so `messageId`
 * matches what Segment receives and both streams can be deduped.
 */
export const createCollectorPlugin = (collectorHost: string): Plugin => {
  let keepaliveBytesInFlight = 0;

  // text/plain avoids a CORS preflight (same as Segment)
  const post = (url: string, body: string, keepalive: boolean) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body,
      keepalive,
    });

  const send = (type: CollectorEventType) => (ctx: Context) => {
    const url = `https://${collectorHost}/${PATHS[type]}`;
    // sentAt lets the collector correct client clock skew, as Segment does
    const body = JSON.stringify({
      ...ctx.event,
      sentAt: new Date().toISOString(),
    });
    const size = new TextEncoder().encode(body).length;
    // keepalive survives page unload, but only while under the browser's budget
    const keepalive = keepaliveBytesInFlight + size <= KEEPALIVE_BUDGET;
    if (keepalive) keepaliveBytesInFlight += size;

    // No retry: a CORS-blocked response also throws TypeError after the
    // collector stored the event, so a retry would double-count it
    post(url, body, keepalive)
      .catch((error: unknown) => {
        console.warn('[Analytics] Failed to send event to collector', error);
      })
      .finally(() => {
        if (keepalive) keepaliveBytesInFlight -= size;
      });
    return ctx;
  };

  return {
    name: 'Libertify Collector',
    type: 'destination',
    version: '1.0.0',
    isLoaded: () => true,
    load: () => Promise.resolve(),
    track: send('track'),
    page: send('page'),
    identify: send('identify'),
    group: send('group'),
    alias: send('alias'),
  };
};
